import type { Dispatch } from '@reduxjs/toolkit';
import type { MutableRefObject } from 'react';

import {
  addMessage,
  attachTraceIdToLatestAssistantMessage,
  attachVisualizationToMessage,
  clearStreamingContent,
  setCurrentTraceId,
  setIsStreaming,
  setToolActivity,
} from '../state/aiSidekickChatSlice';
import type { Message, SidekickError } from '../state/chatTypes';
import type { VisualizationData } from '../structuredQuery/types';
import {
  bufferActionEvent,
  flushActionTurn,
  type StreamActionTurn,
} from './actionTurn';
import type { StreamEvent, StreamVisualizationPosition } from './streamEvent';

/**
 * Terminal signal returned by {@link dispatchStreamEvent}. The caller
 * (the SSE background pump loop) uses this to decide whether to
 * continue reading from the stream, tear down on `done`, reconnect on
 * a BE-requested recoverable error, or surface a terminal `error`
 * frame.
 */
export type DispatchResult = 'continue' | 'done' | 'error' | 'reconnect';

/**
 * Buffered table/config visualization awaiting flush on `messageDone`.
 *
 * Today the package attaches only the first supported table payload to
 * `Message.visualization`; server-rendered PNG refs arrive separately on
 * `Message.visualizationUrls` in the final `messageDone.message` payload.
 */
export type StreamVisualizationBufferEntry = {
  visualization: VisualizationData;
  position: StreamVisualizationPosition;
};

type TableVisualizationData = Extract<VisualizationData, { type: 'table' }>;

const isTableVisualization = (
  visualization: VisualizationData
): visualization is TableVisualizationData => visualization.type === 'table';

const INTERMEDIATE_MESSAGE_NAMES = new Set([
  // TODO(SDPCDXP-1499): Replace this FE-maintained denylist with an
  // explicit BE user-facing/intermediate marker when the stream contract
  // exposes one.
  'executesql',
  'execute_sql',
  'generatedsql',
  'generated_sql',
  'nl2sql',
  'sql',
  'sqlquery',
  'sql_query',
  'validatesql',
  'validate_sql',
]);

/**
 * Side-effect bag handed to the dispatcher by the SSE hook.
 * `lastEventIdRef` is updated from `StreamEvent.eventId` on every frame
 * that carries one, so mid-turn reconnect can pass `lastEventId` as a
 * query param.
 */
export type DispatchContext = {
  dispatch: Dispatch;
  /** Returns false after the transport turn owning this context is superseded. */
  isActive?: () => boolean;
  streamingContentRef: { current: string };
  visualizationBufferRef: MutableRefObject<StreamVisualizationBufferEntry[]>;
  finalizedMessagesAwaitingVisualizationRef: MutableRefObject<Set<string>>;
  lastEventIdRef: MutableRefObject<string | undefined>;
  /** Action frames held until the turn ends. */
  actionTurnRef: MutableRefObject<StreamActionTurn>;
  onDone?: (traceId: string) => void;
  onError?: (error: SidekickError) => void;
};

const mergeVisualizationOntoMessage = (
  message: Message,
  buffer: StreamVisualizationBufferEntry[]
): Message => {
  if (message.visualization !== undefined) {
    return message;
  }
  const firstTable = buffer.find(({ visualization }) =>
    isTableVisualization(visualization)
  );
  if (firstTable === undefined) {
    return message;
  }
  // The initial table renderer supports one structured payload per
  // assistant turn. Non-table variants are reserved for future chart
  // rendering, and additional tables are ignored until a multi-table
  // model is designed.
  return {
    ...message,
    visualization: firstTable.visualization,
  };
};

const normalizeMessageName = (name: string): string =>
  name.toLowerCase().replace(/[\s-]+/g, '_');

const getFirstPendingMessageId = (
  pendingMessageIds: ReadonlySet<string>
): string | undefined => {
  const first = pendingMessageIds.values().next();
  return first.done === true ? undefined : first.value;
};

/** Returns true for backend debug/intermediate messages hidden from users. */
export const isIntermediateSidekickMessage = (message: Message): boolean => {
  if (message.name === undefined || message.name === '') {
    return false;
  }
  return INTERMEDIATE_MESSAGE_NAMES.has(normalizeMessageName(message.name));
};

/**
 * Routes one decoded {@link StreamEvent} to the slice and orchestrator
 * callbacks. Captures a non-empty `eventId` into `lastEventIdRef`.
 */
export const dispatchStreamEvent = (
  event: StreamEvent,
  ctx: DispatchContext
): DispatchResult => {
  if (ctx.isActive?.() === false) {
    return 'done';
  }
  // An actionResult frame is written past the relay and has no eventId;
  // a reconnect must still resume from the last frame the relay numbered.
  if (event.eventId !== '') {
    ctx.lastEventIdRef.current = event.eventId;
  }

  if (bufferActionEvent(event, ctx.actionTurnRef.current)) {
    return 'continue';
  }

  if ('textDelta' in event) {
    ctx.streamingContentRef.current += event.textDelta.delta;
    // Text deltas are intentionally buffered only. The current API
    // contract does not tag a delta with the producing message name, so
    // rendering it immediately can expose NL2SQL/debug assumptions before
    // the following messageDone frame tells us whether the complete
    // message is user-facing.
    return 'continue';
  }

  if ('toolActivity' in event) {
    ctx.dispatch(
      setToolActivity({
        toolName: event.toolActivity.toolName,
        status: event.toolActivity.status,
        description: event.toolActivity.description,
      })
    );
    return 'continue';
  }

  if ('visualization' in event) {
    const visualization = event.visualization.visualization;
    const pendingMessageId = getFirstPendingMessageId(
      ctx.finalizedMessagesAwaitingVisualizationRef.current
    );
    if (pendingMessageId !== undefined) {
      ctx.finalizedMessagesAwaitingVisualizationRef.current.delete(
        pendingMessageId
      );
      if (!isTableVisualization(visualization)) {
        return 'continue';
      }
      ctx.dispatch(
        attachVisualizationToMessage({
          messageId: pendingMessageId,
          visualization,
        })
      );
      return 'continue';
    }
    ctx.visualizationBufferRef.current.push({
      visualization,
      position: event.visualization.position,
    });
    return 'continue';
  }

  if ('messageDone' in event) {
    if (isIntermediateSidekickMessage(event.messageDone.message)) {
      ctx.dispatch(clearStreamingContent());
      ctx.streamingContentRef.current = '';
      return 'continue';
    }
    const finalized = mergeVisualizationOntoMessage(
      event.messageDone.message,
      ctx.visualizationBufferRef.current
    );
    ctx.dispatch(addMessage(finalized));
    ctx.dispatch(clearStreamingContent());
    ctx.dispatch(setToolActivity(null));
    ctx.streamingContentRef.current = '';
    ctx.visualizationBufferRef.current = [];
    if (finalized.role === 'assistant') {
      ctx.actionTurnRef.current.answerId = finalized.id;
    }
    if (
      finalized.role === 'assistant' &&
      finalized.visualization === undefined
    ) {
      ctx.finalizedMessagesAwaitingVisualizationRef.current.add(finalized.id);
    } else {
      ctx.finalizedMessagesAwaitingVisualizationRef.current.delete(
        finalized.id
      );
    }
    return 'continue';
  }

  if ('done' in event) {
    const traceId = event.done.traceId;
    flushActionTurn(ctx.actionTurnRef, ctx.dispatch);
    ctx.dispatch(setCurrentTraceId(traceId));
    if (traceId !== '') {
      ctx.dispatch(attachTraceIdToLatestAssistantMessage(traceId));
    }
    ctx.dispatch(setIsStreaming(false));
    ctx.finalizedMessagesAwaitingVisualizationRef.current.clear();
    ctx.onDone?.(traceId);
    return 'done';
  }

  if ('error' in event) {
    if (event.error.shouldReconnect) {
      return 'reconnect';
    }
    const streamError = event.error.error;
    const sidekickError: SidekickError = {
      code: streamError.code,
      message: streamError.message,
      ...(typeof streamError.retryable === 'boolean'
        ? { retryable: streamError.retryable }
        : {}),
      ...(typeof streamError.retryAfterSeconds === 'number'
        ? { retryAfterSeconds: streamError.retryAfterSeconds }
        : {}),
    };
    flushActionTurn(ctx.actionTurnRef, ctx.dispatch);
    ctx.dispatch(clearStreamingContent());
    ctx.dispatch(setToolActivity(null));
    ctx.streamingContentRef.current = '';
    ctx.visualizationBufferRef.current = [];
    ctx.finalizedMessagesAwaitingVisualizationRef.current.clear();
    ctx.onError?.(sidekickError);
    return 'error';
  }

  return 'continue';
};
